import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  selectSelectedElement,
  selectSequenceVariables,
} from "../../../app/slices/TrainingSlice";
import { GetNewGuid } from "../../../helpers/GuidHelper";
import Interaction from "../../../models/interactions/Interaction";
import { ISequence } from "../../../models/interactions/Sequence";
import { ISwitch, SwitchCase } from "../../../models/interactions/Switch";
import { VariableType } from "../../../models/interactions/Variable";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import { useUpdateActiveSequence } from "../useUpdateActiveSequence.hook";

export function formatMatchForInput(match: SwitchCase["match"]): string {
  if (typeof match === "boolean") return match ? "true" : "false";
  if (typeof match === "number") return String(match);
  return match ?? "";
}

export function normalizeMatchFromInput(
  value: string,
  variableType: VariableType | null
): SwitchCase["match"] {
  if (!variableType) return "";
  if (
    variableType === VariableType.String ||
    variableType === VariableType.BuiltIn
  )
    return value;
  if (variableType === VariableType.Number) {
    if (value.trim() === "") return "";
    const parsed = Number(value);
    return isNaN(parsed) ? "" : parsed;
  }
  if (variableType === VariableType.Boolean) {
    return value === "true";
  }
  return value;
}

function castMatchToType(
  match: SwitchCase["match"],
  variableType: VariableType | null
): SwitchCase["match"] {
  if (!variableType) {
    return "";
  }

  if (
    variableType === VariableType.String ||
    variableType === VariableType.BuiltIn
  ) {
    return match === null || match === undefined ? "" : String(match);
  }

  if (variableType === VariableType.Number) {
    if (typeof match === "number" && !isNaN(match)) return match;
    if (typeof match === "string" && match.trim() !== "") {
      const parsed = Number(match);
      return isNaN(parsed) ? 0 : parsed;
    }
    if (typeof match === "boolean") {
      return match ? 1 : 0;
    }
    return 0;
  }

  if (variableType === VariableType.Boolean) {
    if (typeof match === "boolean") return match;
    if (typeof match === "string") {
      const lower = match.toLowerCase();
      if (lower === "true") return true;
      if (lower === "false") return false;
    }
    if (typeof match === "number") {
      return match !== 0;
    }
    return false;
  }

  return match;
}

export default function useSwitchNodePreview(
  nodeId: string,
  interaction: Interaction
) {
  const switchNode = interaction as ISwitch;
  const { updateInteraction } = useUpdateSequenceInteraction();
  const sequence = useSelector(selectSelectedElement) as ISequence | undefined;
  const variables = useSelector(selectSequenceVariables);
  const { updateSequence } = useUpdateActiveSequence();

  const selectedVariable = useMemo(
    () => variables.find((v) => v.id === switchNode.variable),
    [variables, switchNode.variable]
  );

  const variableType = selectedVariable?.type ?? null;

  const castCases = (cases: SwitchCase[], type: VariableType | null) =>
    cases.map((caseItem) => ({
      ...caseItem,
      match: castMatchToType(caseItem.match, type),
    }));

  const handleVariableSelect = (variableId: string) => {
    const nextVariable = variables.find((v) => v.id === variableId);
    const nextType = nextVariable?.type ?? null;
    const hasTypeChanged = variableType !== nextType;
    const nextCases = hasTypeChanged
      ? castCases(switchNode.cases, nextType)
      : switchNode.cases;

    updateInteraction(nodeId, {
      ...switchNode,
      variable: variableId,
      cases: nextCases,
    } as ISwitch);
  };

  const updateCase = (updatedCase: SwitchCase) => {
    const nextCases = switchNode.cases.map((caseItem) =>
      caseItem.id === updatedCase.id ? updatedCase : caseItem
    );
    updateInteraction(nodeId, {
      ...switchNode,
      cases: nextCases,
    } as ISwitch);
  };

  const handleAddCase = () => {
    const defaultMatch =
      variableType === VariableType.Number
        ? 0
        : variableType === VariableType.Boolean
          ? false
          : "";
    const nextCases = [
      ...switchNode.cases,
      { id: GetNewGuid(), match: defaultMatch },
    ];
    updateInteraction(nodeId, {
      ...switchNode,
      cases: nextCases,
    } as ISwitch);
  };

  const handleDeleteCase = (caseId: string) => {
    if (switchNode.cases.length <= 1) {
      return;
    }

    if (!sequence?.nodes || !sequence?.edges) {
      return;
    }

    const copy = structuredClone(sequence) as ISequence;
    const node = copy.nodes.find((n) => n.id === nodeId);
    if (!node) {
      return;
    }

    const nodeInteraction = node.data.interaction as ISwitch;
    const nextCases = nodeInteraction.cases.filter(
      (caseItem) => caseItem.id !== caseId
    );

    node.data.interaction = {
      ...nodeInteraction,
      cases: nextCases,
    } as ISwitch;

    copy.edges = copy.edges.filter(
      (edge) => !(edge.source === nodeId && edge.sourceHandle === caseId)
    );

    updateSequence(copy);
  };

  return {
    switchNode,
    variables,
    variableType,
    handleVariableSelect,
    updateCase,
    handleAddCase,
    handleDeleteCase,
  };
}
